import { network } from "../config";
import {
  BankPayoutInfo,
  BankPayRequest,
  PayRequestsStatuses,
} from "../types/PayoutTypes";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import url from "../utils/formApiUrl";
import customKy from "./kyExtend";

const { payout } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "payout/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

export default class PayoutApi {
  static async saveBankPayoutInfo(
    token: string,
    newPayoutInfo: Omit<BankPayoutInfo, "user" | "_id">
  ): Promise<
    TypeDefaultResponse & {
      payoutInfo?: BankPayoutInfo;
    }
  > {
    return await customKy
      .put(url(baseURL, payout.saveBankPayoutInfo), {
        headers: getAuthHeader(token),
        json: { newPayoutInfo },
      })
      .then((res) => res.json());
  }

  static async getBankPayoutInfo(token: string): Promise<
    TypeDefaultResponse & {
      payoutInfo?: BankPayoutInfo | null;
    }
  > {
    return await customKy
      .get(url(baseURL, payout.getBankPayoutInfo), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async createBankPayRequest(
    token: string,
    amount: number
  ): Promise<
    TypeDefaultResponse & {
      payoutInfo?: BankPayoutInfo;
    }
  > {
    return await customKy
      .post(url(baseURL, payout.createBankPayRequest), {
        headers: getAuthHeader(token),
        json: { amount },
      })
      .then((res) => res.json());
  }

  static async getBankPayRequests(
    token: string,
    page: number,
    limit: number,
    status?: PayRequestsStatuses
  ): Promise<
    TypeDefaultResponse & {
      requests?: BankPayRequest[];
      totalAmount: number;
    }
  > {
    const params = new URLSearchParams();
    if (status) {
      params.append("status", status);
    }
    return await customKy
      .get(url(baseURL, payout.getBankPayRequests, { page, limit }, params), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getMyBankPayRequests(
    token: string,
    status?: PayRequestsStatuses
  ): Promise<
    TypeDefaultResponse & {
      requests?: BankPayRequest[];
    }
  > {
    const formaData = new FormData();
    if (status) {
      formaData.append("status", status);
    }
    return await customKy
      .get(url(baseURL, payout.getMyBankPayRequests), {
        headers: getAuthHeader(token),
        body: formaData,
      })
      .then((res) => res.json());
  }

  static async cancelBankPayRequest(
    token: string,
    requestId: string
  ): Promise<
    TypeDefaultResponse & {
      payoutInfo?: BankPayoutInfo;
    }
  > {
    return await customKy
      .post(url(baseURL, payout.cancelBankPayRequest), {
        headers: getAuthHeader(token),
        json: { requestId },
      })
      .then((res) => res.json());
  }

  static async changeBankPayRequestStatus(
    token: string,
    payload: {
      requestId: string;
      status: PayRequestsStatuses;
      cancelationReason?: string;
    }
  ): Promise<
    TypeDefaultResponse & {
      request?: BankPayRequest | null;
    }
  > {
    return await customKy
      .patch(url(baseURL, payout.changeBankPayRequestStatus), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }
}
